import { Input, Switch, Autocomplete, AutocompleteItem } from '@heroui/react';
import type { BackCushionSpringConfig as BackCushionSpringConfigType } from '@estimator/shared';

interface CushionSpringConfigProps {
  springs: BackCushionSpringConfigType;
  onChange: (springs: BackCushionSpringConfigType) => void;
  /** Available hardware items for spring/clip/wire pickers */
  hardwareOptions: Array<{ id: string; name: string }>;
  /** When true, all controls are non-interactive and the section is dimmed. */
  isDisabled?: boolean;
}

export default function CushionSpringConfig({
  springs,
  onChange,
  hardwareOptions,
  isDisabled = false,
}: CushionSpringConfigProps) {
  const updateField = <K extends keyof BackCushionSpringConfigType>(
    key: K,
    value: BackCushionSpringConfigType[K],
  ) => {
    onChange({ ...springs, [key]: value });
  };

  return (
    <div className="space-y-3" style={{ opacity: isDisabled ? 0.5 : 1 }}>
      <h3 className="text-xs font-semibold" style={{ color: '#e67e22' }}>
        Spring Configuration
      </h3>

      {/* Spring hardware picker */}
      <Autocomplete
        label="Spring Hardware"
        selectedKey={springs.spring_hardware_id ?? undefined}
        onSelectionChange={(key) => updateField('spring_hardware_id', (key as string) || null)}
        size="sm"
        placeholder="Select spring hardware..."
        inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
        isDisabled={isDisabled}
      >
        {hardwareOptions.map((h) => (
          <AutocompleteItem key={h.id}>{h.name}</AutocompleteItem>
        ))}
      </Autocomplete>

      {/* Spring mode + spacing/count */}
      <div className="grid grid-cols-2 gap-3 items-end">
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Spring Mode:</span>
          <Switch
            size="sm"
            isSelected={springs.spring_mode === 'manual'}
            onValueChange={(checked) => updateField('spring_mode', checked ? 'manual' : 'auto')}
            isDisabled={isDisabled}
          >
            <span className="text-xs">{springs.spring_mode === 'manual' ? 'Manual' : 'Auto'}</span>
          </Switch>
        </div>
        {springs.spring_mode === 'auto' ? (
          <Input
            label="Spring Spacing"
            type="number"
            value={(springs.spring_spacing ?? 4).toString()}
            onValueChange={(v) => updateField('spring_spacing', Number(v) || 0)}
            size="sm"
            classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
            endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>}
            isDisabled={isDisabled}
          />
        ) : (
          <Input
            label="Spring Count"
            type="number"
            value={(springs.spring_count ?? 0).toString()}
            onValueChange={(v) => updateField('spring_count', Math.max(0, Math.floor(Number(v) || 0)))}
            size="sm"
            classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
            isDisabled={isDisabled}
          />
        )}
      </div>

      {/* Clip hardware */}
      <Autocomplete
        label="Clip Hardware"
        selectedKey={springs.clip_hardware_id ?? undefined}
        onSelectionChange={(key) => updateField('clip_hardware_id', (key as string) || null)}
        size="sm"
        placeholder="Select clip hardware..."
        inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
        isDisabled={isDisabled}
      >
        {hardwareOptions.map((h) => (
          <AutocompleteItem key={h.id}>{h.name}</AutocompleteItem>
        ))}
      </Autocomplete>

      {/* Connecting wire hardware + count */}
      <div className="grid grid-cols-2 gap-3">
        <Autocomplete
          label="Connecting Wire Hardware"
          selectedKey={springs.connecting_wire_hardware_id ?? undefined}
          onSelectionChange={(key) => updateField('connecting_wire_hardware_id', (key as string) || null)}
          size="sm"
          placeholder="Select wire hardware..."
          inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
          isDisabled={isDisabled}
        >
          {hardwareOptions.map((h) => (
            <AutocompleteItem key={h.id}>{h.name}</AutocompleteItem>
          ))}
        </Autocomplete>
        <Input
          label="Wire Count"
          type="number"
          value={(springs.connecting_wire_count ?? 1).toString()}
          onValueChange={(v) => updateField('connecting_wire_count', Math.max(0, Math.floor(Number(v) || 0)))}
          size="sm"
          classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
          description="Per section (default 1)"
          isDisabled={isDisabled}
        />
      </div>
    </div>
  );
}
